// @flow
import { View } from "extensions/marionette";
import Radio from "backbone.radio";
import { _ } from "extensions/underscore";
import $ from "jquery";
import {
    captureException,
    captureBreadcrumb,
    captureMessage
} from "extensions/sentry";
import { toast } from "extensions/toast";

import { destroyPopovers } from "common/utils";
import strings from "common/strings";

import { references } from "common/helpers/references";
import { QualifiersNotAllowedCategoryError } from "common/helpers/errors";

import { View as TokenfieldView, Model as TokenfieldModel } from "common/components/tokenfield";

import { Qualifiers } from "./qualifiers";
import { Controls } from "./controls";
import { FiltersMenu } from "./filtersMenu/filtersMenu";
import queryBuilderTemplate from "./queryBuilder.jst";

import { TokenDropMenu } from "./tokenDropMenu";
import { TextInput } from "./textInput";
import { show as saveFilterDialog } from "./savedFilters/saveFilterDialog";

import tokenTemplate from "./token.jst";
import "./queryBuilder.scss";

import type TokenType from "./qualifiers";

const normalizeOperator = function(o, { icon = false } = {}) {
    const operators = {
        "=": "+",
        "!=": "-"
    };
    const icons = {
        "+": "<i class=\"far fa-plus\" aria-hidden=\"true\"></i>",
        "-": "<i class=\"far fa-minus\" aria-hidden=\"true\"></i>"
    };
    const normalizedOperator = operators[o] || o;
    
    if (icon && icons[normalizedOperator]) {
        return icons[normalizedOperator];
    }
    
    return normalizedOperator;
};
const inactiveSibling = function(on: boolean = true, siblingSide: "prev" | "next"): Function {
    return function(e) {
        $(e.currentTarget)[siblingSide]().toggleClass("inactive", on);
    };
};

export const QueryBuilder = View.extend({
    className: "query-builder",
    template: queryBuilderTemplate,
    regions: {
        tokenQualifiers: ".tokens",
        textQualifiers: ".text",
        controls: ".controls",
        filtersMenu: {
            replaceElement: true,
            el: ".filters-menu"
        }
    },
    
    childViewEvents: {
        "mode:change": "setMode",
        clear: "clearQualifiers",
        apply: "applyQualifiers",
        "add:filter": "addFilter",
        "clear:temp:filters": "clearTempFilters",
        "save:filters": "saveFilters",
        "apply:saved:filter": "applySavedFilter"
    },
    
    events: {
        "mouseenter .exclude": inactiveSibling(true, "next"),
        "mouseleave .exclude": inactiveSibling(false, "next"),
        "mouseenter .include": inactiveSibling(true, "prev"),
        "mouseleave .include": inactiveSibling(false, "prev"),
        "mouseup .tt-suggestion .exclude": "excludeNextToken",
        "mouseup .dropdown-menu-list li a .exclude": "excludeNextToken",
        "typeahead:render": function() {
            // Close filter menu when typeahead is active
            this.toggleFilters(false);
        },
        "typeahead:open": function(event) {
            if (this.state.typeaheadMenuMoved) {
                return;
            }
            
            // Move menu
            $(event.target).data("ttTypeahead").menu.$node.appendTo(this.$(".typeahead"));
            this.state.typeaheadMenuMoved = true;
        },
        
        "click .filter-icon": function() {
            this.toggleFilters(this.getChildView("filtersMenu").state.hidden);
        }
    },
    
    initialMode: "token",
    
    savedFilters: true,
    
    initialize(options) {
        this.state = {
            typeaheadMenuMoved: false,
            savedFilter: void 0,
            mode: _.result(this, "initialMode")
            
        };
        this.resetTempQualifiers();
        this.tempDeletedTokens = [];
    },
    
    getInstanceQualifiers() {
        return this.getOption("qualifiers");
    },
    
    checkSavedFilter() {
        if (!this.getOption("savedFilters")) return;
        
        const q_str = this.getInstanceQualifiers().toString();
        const match = Radio.channel("main").request("get:filters").find((f) => f.get("qualifiers") === q_str);
        
        if (match) {
            this.state.savedFilter = match.toJSON();
        } else {
            this.state.savedFilter = void 0;
        }
        
        this.once("render", () => {
            this.$el.toggleClass("applied-saved-filter", !!match);
        });
    },
    
    resetTempQualifiers() {
        this.tempQualifiers = this.getInstanceQualifiers().clone();
        this.tempQualifiers.value = [];
    },
    
    getQualifiers({ json = false } = {}): Qualifiers {
        const q = this.getInstanceQualifiers();
        
        if (json) {
            return q.toJSON();
        }
        
        return q;
    },
    
    hasTempQualifiers() {
        return this.tempQualifiers.value.length > 0;
    },
    
    toggleApplyButtons() {
        if (!this.isRendered()) return;
        
        const hasTemp = this.hasTempQualifiers();
        this.getRegion("controls").currentView.toggleTempModeState(hasTemp);
        this.$el.toggleClass("tempTokensPresent", hasTemp);
    },
    
    focus() {
        this.focusTimeout = setTimeout(() => {
            let input;
            if (this.state.mode === "token" && this.tokensInput && this.tokensInput.isRendered()) {
                input = this.tokensInput.$el.find(".tt-input");
            } else if (this.textInput && this.textInput.isRendered()) {
                input = this.textInput.ui.input;
            }
            
            if (!input || !input.length) {
                return;
            }
            
            input.focus();
            
            // Set cursor at the very end of the input
            const l = input.val().length;
            input[0].setSelectionRange(l, l);
        }, 300); // Delay focus to allow typeahead to init and render
    },
    
    initControls() {
        this.showChildView("controls", new Controls({
            mode: this.state.mode,
            hasSavedFilters: this.getOption("savedFilters"),
            savedFilter: this.state.savedFilter
        }));
        this.setMode(this.state.mode);
    },
    
    computeAllLabels() {
        // Hold a reference to collection here, to be accessible when changing a token value
        this.allLabels = this.getCollection().reduce((memo, model) => {
            const pivotLabels = _.groupBy(_.mapObject(model.get("source").local, (obj) => {
                let name = obj.display || obj.alias;
                if (["reason_code", "mcc"].includes(obj.pivot)) {
                    name = `${obj.value} - ${name}`;
                }
                return {
                    pivot: obj.pivot,
                    name,
                    value: obj.value,
                    typeString: obj.typeString
                };
            }), "pivot");
            
            return {
                ...memo,
                ..._.mapObject(pivotLabels, (value) => _.sortBy(value, "name"))
            };
        }, {});
    },
    
    initTokenfield() {
        this.tokensInput = new TokenfieldView({
            collection: this.getCollection(),
            model: new TokenfieldModel({
                createTokensOnBlur: false,
                placeholder: strings.addAFilter,
                tokens: this.getQualifiers().toTokens(),
                allowEditing: false
            }),
            
            splitter(tokens: string) {
                try {
                    return new Qualifiers({
                        value: tokens
                    }).toTokens();
                } catch {
                    return tokens;
                }
            },
            
            tokenTemplate: tokenTemplate(),
    
            onTokenBeforeCreate: (event) => {
                // Tokenfield wants us to mutate attrs in place. Weird approach...
                let normalized;
                try {
                    normalized = this.normalizeToken({ token: event.attrs });
                } catch (error) {
                    captureException(error);
                    normalized = false;
                }
                
                // If we were unable to normalize the token then don't create it
                if (normalized === false) {
                    event.preventDefault();
                    return;
                }
                
                Object.assign(event.attrs, normalized);
                
                delete this.excludeNextTokenFlag;
                delete event.attrs.pivot;
                delete event.attrs.alias;
            },
            
            onTokenCreate: (event) => {
                const data = event.attrs;
                const $token = $(event.relatedTarget);
                if (!data.applied) {
                    $token.addClass("not-applied");
                }
                $token.find(".operator").html(normalizeOperator(data.operator, { icon: true }));
                
                // Hide chevron if no other choices
                if (!this.allLabels[data.category] || this.allLabels[data.category].length === 1) {
                    $token.find(".chevron").remove();
                }
            },
            
            onOperatorClick: (event) => {
                const data = event.attrs;
                if (!data.applied) {
                    return false;
                }
                const qualifiers: Qualifiers = this.getQualifiers();
                const newOperator = data.operator === "=" ? "!=" : "=";
                qualifiers.remove(`${data.category}`, { value: data.ref });
                qualifiers.add(`${normalizeOperator(newOperator)}${data.category}:${Qualifiers.normalizeValue(data.ref)}`);
                this.triggerMethod("updated:input", qualifiers);
            },
            
            onTokenClick: (event) => {
                if (this.tempQualifiers.value.length > 0) {
                    return;
                }
    
                // If event is coming from the dropdown then ignore it
                if (this.$(event.originalTarget).closest(".token-menu-drop").length) {
                    return;
                }
                
                // If there are no suggestions or only one then return earlier
                const data = event.attrs;
                if (!this.allLabels[data.category] || this.allLabels[data.category].length === 1) {
                    return;
                }
                
                const tokenMenu = new TokenDropMenu({
                    labels: this.allLabels[data.category],
                    data,
                    onChangeValue: (newValue) => {
                        const qualifiers: Qualifiers = this.getQualifiers();
                        qualifiers.remove(`${data.category}`, { value: data.ref });
                        qualifiers.add(`${normalizeOperator(data.operator)}${data.category}:${Qualifiers.normalizeValue(newValue)}`);
                        this.triggerMethod("updated:input", qualifiers);
                    },
                    
                    onChangeOperator: () => {
                        this.tokensInput.typeaheadEvents["tokenfield:clickedoperator"]({ attrs: data });
                    }
                });
                tokenMenu.render();
                this.$(event.relatedTarget).append(tokenMenu.$el);
            },
            
            onUpdated: (attrs, tokens) => {
                this.getChildView("controls").setClearVisibility({ show: !!tokens.length });
            },
            
            onTokenDelete: (event) => {
                this.tokenDeleted = event.attrs;
                this.tokenDeletedWasApplied = event.attrs.applied;
            },
            
            onChange: ({ allTokens }) => {
                destroyPopovers();
                const tempTokens = allTokens.filter((t) => t.applied === false);
                
                if (this.tokenDeleted) {
                    if (!this.tokenDeletedWasApplied) {
                        this.tempQualifiers.clean();
                        this.tempQualifiers.add(this.constructor.toQualifiers(_.filter(allTokens, (t) => t.applied === false)));
                        this.tokensInput.addUnappliedToken(allTokens);
                        this.toggleApplyButtons();
                    } else if (tempTokens.length) {
                        this.tempDeletedTokens.push(this.tokenDeleted);
                    } else {
                        this.triggerMethod("updated:input", this.constructor.toQualifiers(_.filter(allTokens, (t) => t.applied === true)), { deleted: this.tokenDeleted });
                    }
                    this.tokenDeleted = false;
                    this.tokenDeletedWasApplied = true;
                } else {
                    // this happens if they have temps and then use the typeahead
                    if (tempTokens.length) {
                        allTokens[allTokens.length - 1].applied = false;
                        this.tempQualifiers.clean();
                        this.tempQualifiers.add(this.constructor.toQualifiers(_.filter(allTokens, (t) => t.applied === false)));
                        this.tokensInput.addUnappliedToken(allTokens);
                        return;
                    }
                    this.triggerMethod("updated:input", this.constructor.toQualifiers(allTokens));
                }
            },
            
            onShowAll: (event) => this.toggleFilters(true),
            
            onHideAll: (event) => {
                if (event.keyCode === 13 && this.tempQualifiers.value.length > 0) {
                    this.toggleFilters(false);
                    this.applyTempQualifiers();
                }
            }
        });
        
        if (this.isRendered()) {
            this.showChildView("tokenQualifiers", this.tokensInput);
        }
        
        this.$el.addClass("tokenfield-loaded");
    },
    
    excludeNextToken(event) {
        this.excludeNextTokenFlag = true;
    },
    
    initTextSearch() {
        this.textInput = new TextInput({
            qualifiers: this.getQualifiers().toString()
        });
        
        this.showChildView("textQualifiers", this.textInput);
    },
    
    clearQualifiers() {
        if (this.tempQualifiers.value.length > 0) {
            this.clearTempFilters();
        }
        const qualifiers: Qualifiers = this.getQualifiers();
        qualifiers.clean();
        this.triggerMethod("updated:input", qualifiers);
        this.tokensInput.el.click();
    },
    
    applyQualifiers() {
        if (this.hasTempQualifiers()) {
            this.applyTempQualifiers();
            this.resetTempQualifiers();
            this.toggleApplyButtons();
            return;
        }
        
        const qualifiersStr: string = this.textInput.getValue();
        captureBreadcrumb({
            message: "Applying qualifiers",
            category: "analytics",
            data: {
                qualifiersStr
            }
        });
        
        if (Qualifiers.isValid(qualifiersStr)) {
            const qualifiers: Qualifiers = this.getQualifiers();
            qualifiers.clean();
            try {
                qualifiers.add(qualifiersStr);
                this.triggerMethod("updated:input", qualifiers);
            } catch (error) {
                if (error instanceof QualifiersNotAllowedCategoryError) {
                    // Display message about invalid token
                    toast.error(`Filter ${error.extra.category} is invalid`);
                }
                captureException(error);
            }
        } else {
            // TODO: journey Handle bad input here
        }
    },
    
    setMode(mode: string) {
        this.state.mode = mode;
    
        this.$el.toggleClass("mode-token", this.state.mode === "token")
            .toggleClass("mode-text", this.state.mode === "text");
    },
    
    normalizeToken({ token }) {
        const _tokenClone = {};
        
        // There are two normalization flows so far:
        // First, is when we normalize input from typeahead. And second is when you copy/paste tokens.
        if (token.value === token.label && !token.category && !token.pivot) {
            if (Qualifiers.isValid(token.value)) {
                const q = new Qualifiers({
                    value: token.value
                });
                const tokens = q.toTokens();
                Object.assign(_tokenClone, _.first(tokens) || {});
            } else {
                throw new Error(`Unable to normalize provided token: ${token.value}`);
            }
        } else {
            // Category
            _tokenClone.category = token.category || token.pivot;
            
            // Operator
            _tokenClone.operator = this.excludeNextTokenFlag || token.operator === "!=" ? "!=" : "=";
            
            // Label
            _tokenClone.label = token.label || token.alias;
            
            // Value
            // Check if token value is a qualifier already
            if (Qualifiers.isValid(token.value)) {
                _tokenClone.value = token.value;
            } else {
                _tokenClone.value = `${normalizeOperator(_tokenClone.operator)}${_tokenClone.category}:${Qualifiers.normalizeValue(token.value)}`;
            }
        }
        
        // Find alias for a given value
        let label;
        if (token.alias) {
            label = token.alias;
        } else {
            const qualifier = new Qualifiers(_tokenClone);
            const refValue = qualifier.get(_tokenClone.category)[0];
            if (typeof refValue === "string") {
                const ref = refValue.replace(/&quot;/g, "\"").replace(/&#x27;/g, "'");
                // Tokenfield is using jquery.text to set token label. We should unescape the output, otherwise it will be double escaped
                label = _.unescape(references.toAlias(ref, _tokenClone.category));
            } else {
                captureMessage("Wrong type of refValue. Expected string", { level: "warning" });
            }
        }
        
        _tokenClone.label = label;
        
        // It's a good idea to run some validations here to check if category and operator are supported and the value is set
        
        return _tokenClone;
    },
    
    toggleFilters(show) {
        if (!show && this.getOption("alwaysShowFilters")) return;
        
        if (show) {
            this.getChildView("filtersMenu").show();
        } else {
            this.getChildView("filtersMenu").hide();
        }
    },
    
    addFilter({ model, operator, state }) {
        destroyPopovers();
    
        const qualifiers = this.getQualifiers();
        const tempQualifiers = this.tempQualifiers;
        
        const q_str = `${operator === "excluded" ? "-" : "+"}${model.get("pivot")}:${Qualifiers.normalizeValue(model.get("value"))}`;
        if (state.multi) {
            tempQualifiers.add(q_str);
            delete this.excludeNextTokenFlag; // TODO - this only needed for typeahead now..refactor later to get rid of.
            // Now merge temporary tokens with applied tokens and set them to tokenfield
            const tempTokens = tempQualifiers.toTokens().map((t) => ({ ...t, applied: false }));
            this.tokensInput.addUnappliedToken([...qualifiers.clone().toTokens(), ...tempTokens]);
            
            // Set state of the apply button
            this.toggleApplyButtons();
            
            // Focus back to the input
            this.focus();
        } else {
            qualifiers.add(q_str);
            this.triggerMethod("updated:input", qualifiers);
        }
    },
    
    applyTempQualifiers() {
        const permTokens = this.getQualifiers().toTokens();
        const tempTokens = this.tempQualifiers.toTokens();
        const cleanedTokens = permTokens.filter((t) => !this.tempDeletedTokens.some((d) => d.value === t.value));
        this.tempDeletedTokens = [];
        const finalTokens = _.sortBy(cleanedTokens.concat(tempTokens), "category");
        this.triggerMethod("updated:input", this.constructor.toQualifiers(finalTokens));
    },
    
    clearTempFilters() {
        // Clear the input first
        this.tokensInput.clear();
        
        // Then apply existing real qualifiers
        this.tokensInput.addUnappliedToken(this.getQualifiers().toTokens());
        
        // Reset temporary qualifiers to their default state
        this.resetTempQualifiers();
        
        // Reset apply button appearance
        this.toggleApplyButtons();
    },
    
    initFilters() {
        this.filtersMenu = new FiltersMenu({
            parent: this,
            savedFilters: this.getOption("savedFilters"),
            alwaysShowFilters: this.getOption("alwaysShowFilters")
        });
        
        this.showChildView("filtersMenu", this.filtersMenu);
    },
    
    saveFilters() {
        // We should ignore temporary filters that are not applied
        saveFilterDialog({
            qualifiers: this.getQualifiers(),
            success: () => {
                // Re-render itself on success.
                // This should take care of applying saved filter
                this.render();
            }
        });
    },
    
    applySavedFilter(saved) {
        this.triggerMethod("updated:input", saved);
    },
    
    setCollection(): Promise<boolean> {
        return Promise.resolve(true);
    },
    
    getCollection() {
        if (this.collection) {
            return this.collection;
        }
        
        throw new Error("Collection is undefined. Have you implemented `setCollection` method?");
    },
    
    templateContext() {
        this.checkSavedFilter();
    
        return {
            state: this.state
        };
    },
    
    onDestroy() {
        this.availableFilters = null;
        if (this.focusTimeout) {
            clearTimeout(this.focusTimeout);
        }
        
        if (this.initPromise) {
            this.initPromise.cancel();
        }
    },
    
    onRender() {
        this.initTextSearch();
        this.initControls();
        
        this.initPromise = this.setCollection()
            .then(this.computeAllLabels.bind(this))
            .then(this.initTokenfield.bind(this))
            .then(this.initFilters.bind(this))
            .then(() => {
                if (this.getOption("onRenderFocus")) {
                    this.focus();
                }
            });
    }
}, {
    /**
     * Convert qualifiers string to consumable object by the views
     * @param qualifiersString
     */
    parse: Qualifiers.parser,
    
    /**
     * Convert qualifiers object to string
     * @param qualifiersObject
     */
    stringify: Qualifiers.serializer,
    
    /**
     * Convert Tokenfield tokens to Qualifier object
     * @param tokens
     */
    toQualifiers(t: Array<TokenType>): Qualifiers {
        return new Qualifiers({
            value: t.map((token: TokenType) => token.value).join(" ")
        });
    }
});
